<script lang="ts" setup>
import one from '@/assets/images/about/100.png'
import two from '@/assets/images/about/550.png'
import { vLazyVideo } from '@/composables/useLazyVideo'
const { trackEvent } = useCnzz()

const { t } = useI18n()
const { webImages, mobileImages } = useAwardImages()

definePageMeta({ layout: 'page' })
useHead({
  title: t('about.pageTitle'),
  meta: [
    {
      name: 'description',
      content: t('seo.about.description'),
    },
    {
      name: 'keywords',
      content: t('seo.about.keywords'),
    },
    {
      property: 'og:title',
      content: t('seo.about.title'),
    },
    {
      property: 'og:description',
      content: t('seo.about.description'),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      property: 'og:locale',
      content: 'zh_CN',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: t('seo.about.title'),
    },
    {
      name: 'twitter:description',
      content: t('seo.about.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      name: 'author',
      content: '玻色量子',
    },
  ],
})

const { getNewsList } = useNewsApi()
// Web端分页
const currentPage = ref(1)
const size = 4
// 使用 useFetch 获取新闻列表数据（传入响应式的 currentPage）
// 添加 server: false 确保只在客户端执行，避免打包后 SSR 问题
const { data, pending, error, refresh } = await getNewsList(currentPage, size, {
  server: false,
  immediate: true,
})

// 确保在客户端挂载时触发初始请求（解决打包后首次加载不请求的问题）
onMounted(() => {
  // onMounted 只在客户端执行，强制触发请求确保数据加载
  nextTick(() => {
    // 检查数据是否已加载
    const responseData = (data.value as any)?.data
    const hasData =
      responseData?.data?.length > 0 ||
      (Array.isArray(responseData) && responseData.length > 0)

    // 如果没有数据且不在加载中，则触发请求
    if (!hasData && !pending.value) {
      refresh()
    }

    // 调试信息（开发环境）
    if (process.dev) {
      /* empty */
    }
  })
})

// 新闻列表数据 - 根据实际 API 返回结构调整
const newsItems = computed(() => {
  const responseData = (data.value as any)?.data
  // 兼容不同的数据结构：data.data.data 或 data.data
  if (responseData?.data && Array.isArray(responseData.data)) {
    return responseData.data
  }
  if (Array.isArray(responseData)) {
    return responseData
  }
  return []
})

// 格式化发布时间为年月日
const formatDate = (datetime: string | undefined) => {
  if (!datetime) return '--'
  // 如果是完整的日期时间字符串，提取年月日部分
  return datetime.split(' ')[0]
}

// 发展历程数据
const allYears = [2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027]
const disabledYears = [2027] // 禁用的年份
const selectedYear = ref(2026)

// 中心波浪强度 (0-1)，用于淡入淡出动画
const centerWaveIntensity = ref(0)
let centerWaveAnimationId: number | null = null
let centerWaveFadeOutTimer: ReturnType<typeof setTimeout> | null = null

// 触发中心波浪动画
const triggerCenterWave = () => {
  // 清除之前的动画和定时器
  if (centerWaveAnimationId) {
    cancelAnimationFrame(centerWaveAnimationId)
    centerWaveAnimationId = null
  }
  if (centerWaveFadeOutTimer) {
    clearTimeout(centerWaveFadeOutTimer)
    centerWaveFadeOutTimer = null
  }

  // 重置强度，确保从0开始
  centerWaveIntensity.value = 0

  // 淡入动画
  const fadeIn = () => {
    if (centerWaveIntensity.value < 1) {
      centerWaveIntensity.value = Math.min(1, centerWaveIntensity.value + 0.1)
      centerWaveAnimationId = requestAnimationFrame(fadeIn)
    } else {
      // 淡入完成后，设置淡出定时器
      centerWaveFadeOutTimer = setTimeout(() => {
        const fadeOut = () => {
          if (centerWaveIntensity.value > 0.01) {
            // 使用缓动函数，让淡出更平滑
            centerWaveIntensity.value *= 0.92
            centerWaveAnimationId = requestAnimationFrame(fadeOut)
          } else {
            centerWaveIntensity.value = 0
            centerWaveAnimationId = null
          }
        }
        fadeOut()
      }, 300)
    }
  }
  fadeIn()
}

// 计算选中年份的索引位置，用于横轴偏移
const selectedYearIndex = computed(() => allYears.indexOf(selectedYear.value))

// 计算横轴偏移量，使选中年份居中
// 动态计算：中心索引 = (节点数-1)/2，每个节点间距 = 100/节点数
const timelineOffset = computed(() => {
  const nodeCount = allYears.length
  const centerIndex = (nodeCount - 1) / 2 // 8个节点的中心索引是3.5
  const nodeSpacing = 100 / nodeCount // 每个节点占12.5%
  const offset = (centerIndex - selectedYearIndex.value) * nodeSpacing
  return offset
})

// 时间轴事件数据 - 每年6个节点（左侧3个，右侧3个）
interface TimelineEventItem {
  textKey?: string
  text?: string
  link?: string
}

interface TimelineEvent {
  month: string
  description: string
  img: string
  position: 'left' | 'right'
  link?: string
  items?: { text: string; link?: string }[]
}

// 时间轴事件配置 - 只包含结构数据，文本从i18n获取
interface TimelineEventConfig {
  month: string
  img: string
  position: 'left' | 'right'
  link?: string
  items?: TimelineEventItem[]
}

const timelineEventsConfig: Record<number, TimelineEventConfig[]> = {
  2020: [
    {
      month: '09',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/263',
    },
    { month: '11', img: '', position: 'right' },
  ],
  2021: [
    {
      month: '04',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/282',
    },
    { month: '05', img: '', position: 'left' },
    {
      month: '06',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/281',
    },
    {
      month: '09',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/241',
    },
    { 
      month: '12', 
      img: '', 
      position: 'right',
      link: 'https://www.qboson.com/news-v2/237',
    },
  ],
  2022: [
    { month: '01', img: '', position: 'left' },
    {
      month: '03',
      img: '',
      position: 'right',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/232' },
        { textKey: '2' },
      ],
    },
    {
      month: '04',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/284',
    },
    {
      month: '06',
      img: '',
      position: 'right',
    },
    {
      month: '07',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/226',
    },
    {
      month: '10',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/213',
    },
  ],
  2023: [
    {
      month: '01',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/286',
    },
    {
      month: '03',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/288',
    },
    {
      month: '05',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/287',
    },
    {
      month: '09',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/162',
    },
    { 
      month: '11', 
      // img: two, 
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/152',
    },
    {
      month: '12',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/151',
    },
  ],
  2024: [
    {
      month: '04',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/131',
    },
    {
      month: '08',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/107',
    },
    {
      month: '09',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/108',
    },
    {
      month: '10',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/97',
    },
    {
      month: '12',
      img: '',
      position: 'left',
    },
  ],
  2025: [
    {
      month: '05',
      img: '',
      position: 'right',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/73' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/61' },
      ],
    },
    {
      month: '08',
      img: '',
      position: 'left',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/34' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/39' },
      ],
    },
    {
      month: '09',
      img: '',
      position: 'right',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/27' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/28' },
      ],
    },
    {
      month: '10',
      img: '',
      position: 'left',
      link: 'https://www.qboson.com/news-v2/20',
    },
    {
      month: '11',
      img: '',
      position: 'right',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/14' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/8' },
      ],
    },
  ],
  2026: [
    {
      month: '01',
      img: '',
      position: 'left',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/277' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/297' },
      ],
    },
    {
      month: '03',
      img: '',
      position: 'right',
      link: 'https://www.qboson.com/news-v2/323',
    },
    {
      month: '05',
      img: '',
      position: 'left',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/324' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/324' },
      ],
    },
    {
      month: '06',
      img: '',
      position: 'right',
      items: [
        { textKey: '1' },
        { textKey: '2', link: 'https://www.qboson.com/news-v2/325' },
      ],
    },
    {
      month: '07',
      img: '',
      position: 'left',
      items: [
        { textKey: '1', link: 'https://www.qboson.com/news-v2/326' },
        { textKey: '2' },
        { textKey: '3' },
        { textKey: '4', link: 'https://www.qboson.com/news-v2/327' },
      ],
    },
  ],
  2027: [],
}

// 根据配置和当前语言生成带翻译的时间轴事件
const timelineEvents = computed<Record<number, TimelineEvent[]>>(() => {
  const result: Record<number, TimelineEvent[]> = {}
  for (const [year, events] of Object.entries(timelineEventsConfig)) {
    result[Number(year)] = events.map((event) => {
      const items = event.items?.map((item) => ({
        text: item.textKey
          ? t(`about.timeline.${year}.${event.month}_${item.textKey}`)
          : item.text || '',
        link: item.link,
      }))

      return {
        month: event.month,
        description:
          event.month && !items
            ? t(`about.timeline.${year}.${event.month}`)
            : '',
        img: event.img,
        position: event.position,
        link: event.link,
        items,
      }
    })
  }
  return result
})

// 响应式获取当前设备类型的奖项图片
const isMobile = ref(false)
const trophyData = computed(() => (isMobile.value ? mobileImages : webImages))

// 奖项滚动相关
const trophyContainerRef = ref<HTMLElement | null>(null)
const trophyScrollRef = ref<HTMLElement | null>(null)

// 自动滚动控制
const isAutoScrollPaused = ref(false)
const scrollPosition = ref(0)
let scrollAnimationId: number | null = null

// 拖拽/触摸相关
const isDragging = ref(false)
let startX = 0
let startScrollPosition = 0
let resumeTimer: ReturnType<typeof setTimeout> | null = null

// 计算单组图片的总宽度（用于无缝循环）
const singleGroupWidth = computed(() => {
  const itemWidth = isMobile.value ? 178 : 283
  return trophyData.value.length * itemWidth
})

// 规范化滚动位置（保持在有效范围内）
const normalizeScrollPosition = () => {
  const width = singleGroupWidth.value
  if (scrollPosition.value >= width) {
    scrollPosition.value = scrollPosition.value % width
  } else if (scrollPosition.value < 0) {
    scrollPosition.value = width + (scrollPosition.value % width)
  }
}

// 启动滚动动画
const startScrollAnimation = () => {
  const animate = () => {
    if (!isAutoScrollPaused.value && !isDragging.value) {
      scrollPosition.value += 1.8
      normalizeScrollPosition()
    }
    scrollAnimationId = requestAnimationFrame(animate)
  }
  animate()
}

// 暂停自动滚动
const pauseAutoScroll = () => {
  isAutoScrollPaused.value = true
  if (resumeTimer) {
    clearTimeout(resumeTimer)
    resumeTimer = null
  }
}

// 恢复自动滚动（延迟恢复）
const resumeAutoScroll = () => {
  if (resumeTimer) clearTimeout(resumeTimer)
  resumeTimer = setTimeout(() => {
    if (!isDragging.value) {
      isAutoScrollPaused.value = false
    }
  }, 2000) // 2秒后恢复自动滚动
}

// 鼠标拖拽开始
const onMouseDown = (e: MouseEvent) => {
  isDragging.value = true
  startX = e.clientX
  startScrollPosition = scrollPosition.value
  pauseAutoScroll()
}

// 鼠标拖拽移动
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) return
  e.preventDefault()
  const deltaX = startX - e.clientX
  scrollPosition.value = startScrollPosition + deltaX
  normalizeScrollPosition()
}

// 鼠标拖拽结束
const onMouseUp = () => {
  if (isDragging.value) {
    isDragging.value = false
    resumeAutoScroll()
  }
}

// 触摸开始
const onTouchStart = (e: TouchEvent) => {
  isDragging.value = true
  startX = e.touches[0].clientX
  startScrollPosition = scrollPosition.value
  pauseAutoScroll()
}

// 触摸移动
const onTouchMove = (e: TouchEvent) => {
  if (!isDragging.value) return
  const deltaX = startX - e.touches[0].clientX
  scrollPosition.value = startScrollPosition + deltaX
  normalizeScrollPosition()
}

// 触摸结束
const onTouchEnd = () => {
  isDragging.value = false
  resumeAutoScroll()
}

// 检测设备类型
onMounted(() => {
  const checkMobile = () => {
    isMobile.value = window.innerWidth < 768
  }
  checkMobile()
  window.addEventListener('resize', checkMobile)

  // 全局鼠标事件监听（处理拖拽到容器外的情况）
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)

  // 启动滚动动画
  startScrollAnimation()

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobile)
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    if (scrollAnimationId) {
      cancelAnimationFrame(scrollAnimationId)
    }
    if (resumeTimer) {
      clearTimeout(resumeTimer)
    }
  })
})

// 获取当前选中年份的事件
const currentYearEvents = computed(() => {
  const events = timelineEvents.value[selectedYear.value] || []
  const leftEvents = events.filter((e) => e.position === 'left')
  const rightEvents = events.filter((e) => e.position === 'right')
  return { leftEvents, rightEvents, allEvents: events }
})

// 判断左侧描述文本对齐方式：移动端超过13个字符左对齐，桌面端超过33个字符左对齐
const getLeftDescAlign = (description: string) => {
  const len = description.length
  // 移动端：>13左对齐，桌面端：>33左对齐
  if (len > 33) {
    return 'text-left' // 两端都左对齐
  } else if (len > 13) {
    return 'text-left md:text-right' // 移动端左对齐，桌面端右对齐
  } else {
    return 'text-right' // 两端都右对齐
  }
}

// 选择年份
const selectYear = (year: number) => {
  trackEvent('关于波色', '点击', `${year}`)
  if (allYears.includes(year) && !disabledYears.includes(year)) {
    selectedYear.value = year

    // 触发中心波浪动画
    triggerCenterWave()
  }
}

// 荣誉奖项数据
const awards = [
  { id: 1, image: '/images/award1.png', titleKey: 'about.awards.award1' },
  { id: 2, image: '/images/award2.png', titleKey: 'about.awards.award2' },
  { id: 3, image: '/images/award3.png', titleKey: 'about.awards.award3' },
]

// 企业新闻数据
const news = [
  {
    id: 1,
    titleKey: 'about.news.news1',
    date: '2024-01-15',
    image: '/images/news1.png',
    featured: true,
  },
  { id: 2, titleKey: 'about.news.news2', date: '2024-01-10' },
  { id: 3, titleKey: 'about.news.news3', date: '2024-01-05' },
  { id: 4, titleKey: 'about.news.news4', date: '2024-01-01' },
]

// 新闻查看更多 - 友盟上报
const trackEventMore = () => {
  trackEvent('关于波色', '点击', '查看更多按钮')
}

// 社会招聘 - 友盟上报
const trackEventSociety = () => {
  trackEvent('关于波色', '点击', '社会招聘按钮')
}

// 校园招聘 - 友盟上报
const trackEventSchool = () => {
  trackEvent('关于波色', '点击', '校园招聘按钮')
}
</script>

<template>
  <div class="min-h-screen w-full bg-[#F9FBFF]">
    <!-- 顶部横幅图片 -->
    <div class="w-full md:aspect-auto md:h-auto">
      <video
        v-lazy-video
        class="w-full h-[600px] md:h-[487px]"
        data-src="https://images-cloud.qboson.com/website/about.mp4"
        loop
        muted
        playsinline
        webkit-playsinline
        disablepictureinpicture
      ></video>
    </div>
    <!-- 关于玻色量子 -->
    <section
      class="relative bg-[#EAEFF380] rounded-[18px] mx-[18px] px-[20px] py-[30px] mt-[-105px] md:mt-[0px] md:py-16 md:bg-white md:rounded-none md:mx-auto md:px-0"
      style="backdrop-filter: blur(5px)"
    >
      <div class="max-w-[1200px] mx-auto">
        <p class="text-[24px] md:text-[28px] font-bold text-[#000]">
          {{ t('about.title') }}
        </p>
        <div
          class="w-[180px] md:w-[260px] h-[2px] md:h-[5px] my-[10px] md:my-[16px]"
          style="
            background: var(
              --1,
              linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
            );
          "
        ></div>
        <div class="md:hidden">
          <p class="text-[14px] font-medium text-[#333] mb-[10px]">
            {{ t('about.subtitle') }}
          </p>
          <p class="text-[14px] font-medium text-[#333] mb-[10px]">
            {{ t('about.value') }}
          </p>
        </div>
        <p
          class="text-[12px] md:text-lg text-[#444] md:text-[#555] leading-[18px] md:leading-[34px] whitespace-pre-line"
        >
          {{ t('about.description') }}
        </p>
        <div class="hidden md:block">
          <p
            class="mt-[20px] w-fit border-t border-black pt-[6px] text-[14px] font-normal text-[#333333E3] leading-[34px]"
          >
            <span class="text-[16px]">愿景：</span
            >以实用引领量子计算、以量子重新定义人工智能
          </p>
          <p class="text-[14px] font-normal text-[#333333E3] leading-[34px]">
            <span class="text-[16px]">核心价值观：</span
            >客户至上，合作共赢。持续创新，勇攀高峰。勇于担当，树立事业共同体意识。信任和尊重每一个人。脚踏实地，坚持结果导向
          </p>
        </div>
      </div>
    </section>

    <!-- 发展历程 -->
    <section class="relative w-full pt-[76px] overflow-hidden bg-white">
      <p
        class="md:hidden absolute top-[40px] left-1/2 -translate-x-1/2 z-10 text-[16px] font-bold text-[#000] text-center w-full"
      >
        {{ t('about.milestone') }}
      </p>
      <!-- 波浪背景 -->
      <div class="absolute inset-0 z-0">
        <ClientOnly>
          <WaveGridBackground
            color="#36BA93"
            :wave-speed="1"
            :wave-height="0.1"
            :grid-size="100"
            :center-wave-intensity="centerWaveIntensity"
          />
        </ClientOnly>
      </div>

      <!-- 时间轴内容已注释，只保留背景 -->

      <div class="relative z-10 max-w-[1200px] mx-auto px-[16px] md:px-6">
        <div class="relative h-[615px] md:h-[680px]">
          <div
            class="absolute left-1/2 top-[20px] h-[530px] md:h-[538px] w-[1px] md:w-[2px] bg-[#333] transform -translate-x-1/2"
          ></div>
          <div
            class="absolute left-1/2 top-[14px] w-[7px] h-[7px] md:w-[21px] md:h-[21px] year-node-active-Y rounded-full transform -translate-x-1/2 z-20 shadow-lg"
          ></div>
          <div
            class="absolute top-[40px] left-[6px] md:left-0 right-0 h-[480px]"
          >
            <div
              v-for="index in 6"
              :key="`node-${index}`"
              class="relative h-[80px] flex items-center"
              :style="{ zIndex: 7 - index }"
            >
              <div
                class="w-[calc(50%-6px)] flex justify-end pr-[9px] md:pr-[23px] pointer-events-none"
              >
                <Transition name="content-fade" mode="out-in">
                  <div
                    v-if="
                      currentYearEvents.allEvents[6 - index]?.position ===
                        'left' &&
                      currentYearEvents.allEvents[6 - index]?.img == ''
                    "
                    :key="`left-${selectedYear}-${index}`"
                    class="pointer-events-auto rounded-[6px] py-[8px] px-[10px] w-[162px] md:w-[566px] max-w-[162px] md:max-w-[566px]"
                    style="
                      background: rgba(234, 239, 243, 0.5);
                      backdrop-filter: blur(5px);
                    "
                  >
                    <div class="flex items-center justify-end gap-2 mb-1">
                      <div
                        class="w-[30px] md:w-[76px] border-b border-[#000] text-[10px] md:text-[21px] font-normal text-[#000] text-right"
                      >
                        {{ currentYearEvents.allEvents[6 - index].month }}
                      </div>
                    </div>
                    <div
                      :class="[
                        'text-[10px] md:text-base text-[#000] font-normal',
                        getLeftDescAlign(
                          (currentYearEvents.allEvents[6 - index]?.items || [])
                            .map((i) => i.text)
                            .join('') ||
                            currentYearEvents.allEvents[6 - index]
                              ?.description ||
                            '',
                        ),
                      ]"
                    >
                      <template
                        v-if="currentYearEvents.allEvents[6 - index]?.items"
                      >
                        <div
                          v-for="(item, i) in currentYearEvents.allEvents[
                            6 - index
                          ]?.items"
                          :key="i"
                        >
                          <NuxtLink
                            v-if="item.link"
                            :to="item.link"
                            class="cursor-pointer gradient-text-link"
                          >
                            {{ item.text }}
                          </NuxtLink>
                          <span v-else>
                            {{ item.text }}
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <NuxtLink
                          v-if="currentYearEvents.allEvents[6 - index]?.link"
                          :to="currentYearEvents.allEvents[6 - index]?.link"
                          class="cursor-pointer gradient-text-link"
                        >
                          {{
                            currentYearEvents.allEvents[6 - index]?.description
                          }}
                        </NuxtLink>
                        <span v-else>
                          {{
                            currentYearEvents.allEvents[6 - index]?.description
                          }}
                        </span>
                      </template>
                    </div>
                  </div>
                  <div
                    v-else-if="
                      currentYearEvents.allEvents[6 - index]?.position ===
                        'left' &&
                      currentYearEvents.allEvents[6 - index]?.img !== ''
                    "
                    class="pointer-events-auto flex items-center justify-center w-[162px] h-[90px] md:w-[568px] md:h-[236px] rounded-[12px] bg-white bg-cover bg-center bg-no-repeat machine timeline-img"
                    :class="
                      selectedYear === 2024
                        ? 'timeline-img-2024'
                        : 'timeline-img-other'
                    "
                  >
                    <img
                      class="w-[66px] h-[79px] md:w-[173px] md:h-[209px]"
                      :src="currentYearEvents.allEvents[6 - index]?.img"
                      alt=""
                    />
                  </div>
                </Transition>
              </div>
              <div
                class="w-[5px] h-[5px] md:w-[13px] md:h-[13px] bg-white border-[1px] md:border-2 border-[#333] rounded-full flex-shrink-0"
              ></div>
              <div
                class="w-[calc(50%-6px)] pl-[9px] md:pl-[23px] pointer-events-none"
              >
                <Transition name="content-fade" mode="out-in">
                  <div
                    v-if="
                      currentYearEvents.allEvents[6 - index]?.position ===
                      'right'
                    "
                    :key="`right-${selectedYear}-${index}`"
                    class="pointer-events-auto rounded-[6px] py-[8px] px-[10px] w-[162px] md:w-[566px] max-w-[162px] md:max-w-[566px]"
                    style="
                      background: rgba(234, 239, 243, 0.5);
                      backdrop-filter: blur(5px);
                    "
                  >
                    <div class="flex flex-col gap-[8px] mb-[8px]">
                      <div
                        class="w-[30px] md:w-[76px] border-b border-[#000] text-[10px] md:text-[21px] font-normal text-[#000]"
                      >
                        {{ currentYearEvents.allEvents[6 - index].month }}
                      </div>
                    </div>
                    <div
                      class="text-[10px] md:text-base text-[#000] font-normal"
                    >
                      <template
                        v-if="currentYearEvents.allEvents[6 - index]?.items"
                      >
                        <div
                          v-for="(item, i) in currentYearEvents.allEvents[
                            6 - index
                          ]?.items"
                          :key="i"
                        >
                          <NuxtLink
                            v-if="item.link"
                            :to="item.link"
                            class="cursor-pointer gradient-text-link"
                          >
                            {{ item.text }}
                          </NuxtLink>
                          <span v-else>
                            {{ item.text }}
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <NuxtLink
                          v-if="currentYearEvents.allEvents[6 - index]?.link"
                          :to="currentYearEvents.allEvents[6 - index]?.link"
                          class="cursor-pointer gradient-text-link"
                        >
                          {{
                            currentYearEvents.allEvents[6 - index]?.description
                          }}
                        </NuxtLink>
                        <span v-else>
                          {{
                            currentYearEvents.allEvents[6 - index]?.description
                          }}
                        </span>
                      </template>
                    </div>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
          <div
            class="absolute bottom-[-5px] md:bottom-[60px] left-0 right-0 h-[80px] overflow-hidden"
          >
            <div
              class="absolute top-[8px] md:top-[16px] left-0 right-0 h-[1px] md:h-[2px] bg-[#333]"
            ></div>
            <div
              class="absolute left-0 right-0 flex items-start h-full transition-transform duration-500 ease-out"
              :style="{ transform: `translateX(${timelineOffset}%)` }"
            >
              <div
                v-for="year in allYears"
                :key="`node-${year}`"
                :class="[
                  'flex-1 flex justify-center',
                  disabledYears.includes(year)
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer',
                ]"
                @click="selectYear(year)"
              >
                <div
                  class="rounded-full border-2 transition-all duration-300"
                  :class="
                    disabledYears.includes(year)
                      ? 'w-[5px] h-[5px] md:w-[20px] md:h-[20px] bg-white border-[#333] mt-[6px]'
                      : year === selectedYear
                        ? 'w-[7px] h-[7px] md:w-[34px] md:h-[34px] year-node-active border-transparent mt-[4.5px] md:mt-[0px] border-none'
                        : 'w-[5px] h-[5px] md:w-[20px] md:h-[20px] bg-white border-[#333] mt-[6px]'
                  "
                ></div>
              </div>
            </div>
            <div
              class="absolute left-0 right-0 flex items-start top-[32px] transition-transform duration-500 ease-out"
              :style="{ transform: `translateX(${timelineOffset}%)` }"
            >
              <div
                v-for="year in allYears"
                :key="`text-${year}`"
                :class="[
                  'flex-1 flex justify-center',
                  disabledYears.includes(year)
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer',
                ]"
                @click="selectYear(year)"
              >
                <span
                  class="font-normal transition-all duration-300 whitespace-nowrap"
                  :class="
                    disabledYears.includes(year)
                      ? 'text-[#777] text-[12px] md:text-[24px]'
                      : year === selectedYear
                        ? 'text-[#000] font-medium text-[14px] mt-[-2px] md:mt-0 md:text-[28px]'
                        : 'text-[#333] text-[12px] md:text-[24px]'
                  "
                >
                  {{ year }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 团队介绍 -->
    <section
      class="max-w-[1200px] mx-auto pt-[32px] md:pt-0 px-6 pb-12 bg-white md:bg-transparent"
    >
      <p
        class="hidden md:block text-4xl font-bold text-[#000] text-center mt-[60px] mb-[16px]"
      >
        {{ t('about.team.title') }}
      </p>
      <p
        class="md:hidden text-base font-medium text-[#000] text-center mb-[6px] md:mb-[16px]"
      >
        {{ t('about.team.mobileTitle') }}
      </p>
      <div
        class="w-[180px] h-[2px] md:w-[260px] md:h-[5px] m-auto"
        style="
          background: var(
            --1,
            linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
          );
        "
      ></div>
      <p
        class="text-[12px] md:text-[21px] text-[#666] md:text-[#000] font-normal md:leading-[34px] text-center max-w-[1092px] mx-auto pt-[10px] md:pt-[30px]"
      >
        {{ t('about.team.description') }}
      </p>
      <!-- 团队照片占位 -->
      <!-- <div
        class="bg-white h-[176px] md:h-[415px] shadow-img mt-[16px] md:mt-[70px] rounded-xl"
      >
      </div> -->
    </section>

    <!-- 荣誉奖项 -->
    <section class="max-w-[1200px] mx-auto px-6 py-12">
      <p
        class="text-[16px] md:text-4xl font-medium md:font-normal text-[#000] text-center mb-[8px] md:mb-4"
      >
        {{ t('about.awards.title') }}
      </p>
      <div
        class="w-[180px] h-[2px] md:w-[260px] md:h-[5px] m-auto"
        style="
          background: var(
            --1,
            linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
          );
        "
      ></div>

      <!-- 奖项图片展示 -->
      <div
        ref="trophyContainerRef"
        class="mt-[30px] overflow-hidden py-[50px] cursor-grab active:cursor-grabbing select-none"
        @mousedown="onMouseDown"
        @mouseenter="pauseAutoScroll"
        @mouseleave="resumeAutoScroll"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div
          ref="trophyScrollRef"
          class="flex items-center gap-[28px] pointer-events-none"
          :style="{ transform: `translateX(-${scrollPosition}px)` }"
        >
          <!-- 第一组图片 -->
          <div
            v-for="(img, index) in trophyData"
            :key="`first-${index}`"
            class="flex-shrink-0 w-[150px] h-[200px] md:w-[255px] md:h-[340px] rounded-[12px] bg-white flex items-center justify-center"
            :style="{ boxShadow: '0 7px 30px 8px #DAE0ED' }"
          >
            <img
              class="w-[150px] h-[200px] md:w-[300px] md:h-[500px] object-contain"
              :src="img"
              alt=""
            />
          </div>
          <!-- 第二组图片（用于无缝循环） -->
          <div
            v-for="(img, index) in trophyData"
            :key="`second-${index}`"
            class="flex-shrink-0 w-[150px] h-[200px] md:w-[255px] md:h-[340px] rounded-[12px] bg-white flex items-center justify-center"
            :style="{ boxShadow: '0 7px 30px 8px #DAE0ED' }"
          >
            <img
              class="w-[150px] h-[200px] md:w-[300px] md:h-[500px] object-contain"
              :src="img"
              alt=""
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 企业新闻 -->
    <section
      class="bg-white md:bg-[#F9FBFF] max-w-[1200px] mx-auto px-6 py-[40px] md:py-12"
    >
      <p
        class="text-[16px] md:text-4xl font-medium md:font-bold text-[#000] text-center mb-[13px]"
      >
        {{ t('about.news.title') }}
      </p>
      <div
        class="w-[180px] h-[2px] md:w-[260px] md:h-[5px] m-auto"
        style="
          background: var(
            --1,
            linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
          );
        "
      ></div>
      <p
        class="md:hidden text-[12px] text-[#666] font-normal text-center mt-[8px] mb-[15px]"
      >
        一键知晓玻色量子最新动态
      </p>
      <NuxtLink
        :to="`/news-v2`"
        class="block text-[14px] md:text-[16px] m-auto w-[102px] md:w-[144px] text-center py-[6px] mt-[26px] mb-[20px] md:mb-[70px] cursor-pointer gradient-border"
        @click="trackEventMore"
      >
        {{ t('common.viewMore') }}
      </NuxtLink>
      <!-- web端新闻 -->
      <div v-if="newsItems.length > 0" class="hidden md:flex gap-9">
        <!-- 左侧大图新闻 -->
        <div class="w-1/2">
          <NuxtLink
            v-if="newsItems[0]"
            :to="`/news-v2/${newsItems[0].id}`"
            class="block bg-white rounded-[20px] overflow-hidden shadow-[0_7.745px_33.191px_8.851px_#DAE0ED]"
          >
            <div class="w-full h-[312px] flex items-center justify-center">
              <img
                class="w-full h-full"
                :src="newsItems[0]?.cover_image"
                alt=""
              />
            </div>
            <div class="pt-[20px] pb-[38px] px-[30px]">
              <p class="text-sm text-[#6B7280]">
                {{ formatDate(newsItems[0]?.create_datetime) }}
              </p>
              <p class="text-xl font-semibold text-[#000] my-[14px]">
                {{ newsItems[0]?.zh?.title }}
              </p>
              <p
                class="h-[52px] text-base font-normal text-[#4B5563] line-clamp-2"
              >
                {{ newsItems[0]?.zh?.summary }}
              </p>
            </div>
          </NuxtLink>
        </div>

        <!-- 右侧新闻列表 -->
        <div class="w-1/2 flex flex-col justify-between">
          <NuxtLink
            v-for="item in newsItems.slice(1)"
            :key="item.id"
            :to="`/news-v2/${item.id}`"
            class="flex items-center p-5 bg-white rounded-lg shadow-[0_7.745px_33.191px_8.851px_#DAE0ED]"
          >
            <div>
              <p class="text-sm text-[#6B7280]">
                {{ formatDate(item.create_datetime) }}
              </p>
              <h4 class="text-xl text-[#000] font-semibold my-3.5">
                {{ item.zh?.title }}
              </h4>
              <p class="text-xs text-[#999] mb-1 line-clamp-1">
                {{ item.zh?.summary }}
              </p>
            </div>
          </NuxtLink>
        </div>
      </div>

      <!-- 移动端新闻 -->
      <div
        v-if="newsItems.length > 0"
        class="flex md:hidden flex-col gap-[10px]"
      >
        <div class="w-full h-[178px]">
          <NuxtLink
            v-if="newsItems[0]"
            :to="`/news-v2/${newsItems[0].id}`"
            class="bg-white rounded-[4px] overflow-hidden shadow-[0_7.745px_33.191px_8.851px_#DAE0ED]"
          >
            <div
              class="w-full h-[178px] rounded-[4px] flex items-end pb-[8px] justify-center bg-cover bg-center bg-no-repeat"
              :style="{ backgroundImage: `url(${newsItems[0]?.cover_image})` }"
            >
              <p class="text-base font-normal text-white line-clamp-2">
                {{ newsItems[0]?.zh?.title }}
              </p>
            </div>
          </NuxtLink>
        </div>

        <div class="flex flex-col justify-between gap-[10px]">
          <NuxtLink
            v-for="item in newsItems.slice(1)"
            :key="item.id"
            :to="`/news-v2/${item.id}`"
            class="flex items-center gap-[10px] p-5 bg-white rounded-lg shadow-[4px_4px_12px_0px_rgba(87,126,211,0.08)]"
          >
            <div>
              <p class="text-base text-[#000] font-normal line-clamp-2">
                {{ item.zh?.title }}
              </p>
              <p class="text-base text-[#999] font-normal mt-[4px]">
                {{ formatDate(item.create_datetime) }}
              </p>
            </div>
            <img
              class="w-[144px] h-[72px] rounded-[2px]"
              :src="item.cover_image"
              alt=""
            />
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- 加入我们 -->
    <section class="max-w-[1200px] mx-auto px-6 py-12 pb-20">
      <div class="hidden md:block">
        <p
          class="text-[16px] md:text-4xl font-medium md:font-normal text-[#000] text-center mb-[8px] md:mb-4"
        >
          {{ t('about.join.mobileTitle') }}
        </p>
        <div
          class="w-[180px] h-[2px] md:w-[260px] md:h-[5px] m-auto"
          style="
            background: var(
              --1,
              linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
            );
          "
        ></div>
        <p
          class="mx-auto mt-[30px] mb-[100px] max-w-[850px] text-center text-[#000] text-[21px] font-normal leading-[34px]"
        >
          {{ t('about.join.joinDesc') }}
        </p>
      </div>
      <div class="flex flex-col md:flex-row gap-12">
        <!-- 左侧内容 -->
        <div class="flex-1">
          <p class="hidden md:block text-3xl font-bold text-[#000]">
            {{ t('about.join.title') }}
          </p>
          <p class="md:hidden text-base text-center font-medium text-[#000]">
            {{ t('about.join.mobileTitle') }}
          </p>
          <div
            class="md:hidden w-[180px] h-[2px] m-auto mt-[8px] mb-[10px]"
            style="
              background: var(
                --1,
                linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
              );
            "
          ></div>
          <p
            class="text-[12px] md:text-lg text-[#4B5563] text-center md:text-left font-medium mb-[24px] md:my-[28px]"
          >
            {{ t('about.join.description') }}
          </p>

          <!-- 优势列表 -->
          <div class="space-y-4 mb-10">
            <div class="flex items-center md:items-start gap-3">
              <svg
                class="w-[10px] h-[10px] md:w-6 md:h-6 flex-shrink-0 mt-0.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="10" fill="#36BA93" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p
                class="pt-[2px] text-[12px] md:text-lg text-[#666] md:text-[#333]"
              >
                {{ t('about.join.benefits.opportunity.title') }}
                {{ t('about.join.benefits.opportunity.desc') }}
              </p>
            </div>
            <div class="flex items-center md:items-start gap-3">
              <svg
                class="w-[10px] h-[10px] md:w-6 md:h-6 flex-shrink-0 mt-0.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="10" fill="#36BA93" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p
                class="pt-[2px] text-[12px] md:text-lg text-[#666] md:text-[#333]"
              >
                {{ t('about.join.benefits.skill.title') }}
                {{ t('about.join.benefits.skill.desc') }}
              </p>
            </div>
            <div class="flex items-center md:items-start gap-3">
              <svg
                class="w-[10px] h-[10px] md:w-6 md:h-6 flex-shrink-0 mt-0.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="10" fill="#36BA93" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p
                class="pt-[2px] text-[12px] md:text-lg text-[#666] md:text-[#333]"
              >
                {{ t('about.join.benefits.prospect.title') }}
                {{ t('about.join.benefits.prospect.desc') }}
              </p>
            </div>
            <div class="flex items-center md:items-start gap-3">
              <svg
                class="w-[10px] h-[10px] md:w-6 md:h-6 flex-shrink-0 mt-0.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="10" fill="#36BA93" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p
                class="pt-[2px] text-[12px] md:text-lg text-[#666] md:text-[#333]"
              >
                {{ t('about.join.benefits.training.title') }}
                {{ t('about.join.benefits.training.desc') }}
              </p>
            </div>
            <div class="flex items-center md:items-start gap-3">
              <svg
                class="w-[10px] h-[10px] md:w-6 md:h-6 flex-shrink-0 mt-0.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="10" fill="#36BA93" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p
                class="pt-[2px] text-[12px] md:text-lg text-[#666] md:text-[#333]"
              >
                {{ t('about.join.benefits.growth.title') }}
                {{ t('about.join.benefits.growth.desc') }}
              </p>
            </div>
          </div>

          <!-- 职位按钮 -->
          <div class="flex justify-center md:justify-start gap-6">
            <a
              href="https://app.mokahr.com/campus-recruitment/boseq/140969?locale=zh-CN"
              target="_blank"
              class="text-[14px] px-[26px] py-[6px] join-border bg-[#F9FBFF] text-[#333] cursor-pointer"
              @click="trackEventSchool"
            >
              {{ t('about.join.campus') }}
            </a>
            <a
              href="https://app.mokahr.com/social-recruitment/boseq/140968?locale=zh-CN"
              target="_blank"
              class="text-[14px] px-[26px] py-[6px] join-border bg-[#F9FBFF] text-[#333] cursor-pointer"
              @click="trackEventSociety"
            >
              {{ t('about.join.social') }}
            </a>
          </div>
        </div>

        <!-- 右侧图片 -->
        <div class="flex-1 flex items-center justify-center">
          <img
            src="@/assets/images/join.png"
            alt="Join Us"
            class="max-w-full h-auto"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
video {
  display: block;
  /* background-color: #f5f5f5; */
  object-fit: cover;
}

.hover\:shadow-custom:hover {
  box-shadow: 0 7px 30px 8px #dae0ed;
}

.shadow-img {
  box-shadow: 0 7px 30px 8px #dae0ed;
}

/* 波浪背景 */
.wave-bg {
  width: 100%;
  height: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
}

.wave {
  animation: wave-animation 8s ease-in-out infinite;
}

.wave1 {
  animation-delay: 0s;
}

.wave2 {
  animation-delay: -2s;
  animation-duration: 10s;
}

.wave3 {
  animation-delay: -4s;
  animation-duration: 12s;
}

@keyframes wave-animation {
  0%,
  100% {
    d: path(
      'M0,300 C240,200 480,400 720,300 C960,200 1200,400 1440,300 L1440,600 L0,600 Z'
    );
  }
  50% {
    d: path(
      'M0,350 C240,450 480,250 720,350 C960,450 1200,250 1440,350 L1440,600 L0,600 Z'
    );
  }
}

/* 时间轴过渡动画 */
.timeline-enter-active,
.timeline-leave-active {
  transition: all 0.3s ease;
}

.timeline-enter-from,
.timeline-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* 渐变边框圆角 */
.gradient-border {
  position: relative;
  /* background: #f9fbff; */
  z-index: 1;
}

.gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  z-index: -1;
}

.join-border {
  position: relative;
  background: #f9fbff;
  z-index: 1;
}

.join-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  z-index: -1;
}

/* 年份节点渐变背景 */
.year-node-active {
  background: linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%);
}
.year-node-active-Y {
  background: linear-gradient(135deg, #37beef 0%, #47b897 100%);
}

/* 年份文字渐变 */
.year-text-active {
  background: linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 时间轴事件淡入淡出动画 */
.timeline-fade-enter-active,
.timeline-fade-leave-active {
  transition: all 0.4s ease;
}

.timeline-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.timeline-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.timeline-fade-move {
  transition: transform 0.4s ease;
}

/* 年份节点滑动动画 */
.year-slide-enter-active,
.year-slide-leave-active {
  transition: all 0.5s ease;
}

.year-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.year-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.year-slide-move {
  transition: transform 0.5s ease;
}

/* 内容淡入淡出动画 - 只改变透明度，不改变位置 */
.content-fade-enter-active,
.content-fade-leave-active {
  transition: opacity 0.3s ease;
}

.content-fade-enter-from,
.content-fade-leave-to {
  opacity: 0;
}

.machine {
  background-image: url('../../assets/images/about/bg-100.png');
}

/* 时间轴图片样式 */
.timeline-img {
  box-shadow: 0 7px 30px 8px #dae0ed;
}

.timeline-img-2024 {
  margin-bottom: 0px;
}

.timeline-img-other {
  margin-bottom: 0px;
}

@media (min-width: 768px) {
  .timeline-img-2024 {
    /* margin-bottom: 90px; */
    margin-bottom: 58px;
  }
  .timeline-img-other {
    /* margin-bottom: -125px; */
    margin-bottom: 15px;
  }
}

/* 隐藏滚动条 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.gradient-text-link {
  /* background: var(--1, linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)); */
  /* -webkit-background-clip: text; */
  /* background-clip: text; */
  /* -webkit-text-fill-color: transparent; */
  /* color: transparent; */
  position: relative;
  display: inline-block;
  text-decoration: none;
}

.gradient-text-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0px;
  width: 100%;
  height: 1px;
  background: var(
    --1,
    linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%)
  );
}
</style>
